/**
 * TypeScript types for Nameplate Scan API
 * Matches backend serializers from thalo_os/nameplate_scan/serializers.py
 */

/**
 * Individual task status (EXIF extraction, LLM parsing)
 */
export enum TaskStatus {
  PENDING = "pending",
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
}

/**
 * Overall scan status
 */
export enum ScanStatus {
  PROCESSING = "processing",
  COMPLETED = "completed",
  FAILED = "failed",
}

/**
 * Reverse geocoded address structure
 */
export interface ReverseGeocodeAddress {
  street?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
}

/**
 * EXIF extraction result from image metadata
 */
export interface ExifResult {
  // GPS coordinates
  latitude: number | null;
  longitude: number | null;
  altitude: number | null;
  gps_timestamp: string | null;
  has_gps: boolean;

  // Camera metadata
  camera_make: string;
  camera_model: string;
  image_datetime: string | null;

  // Location matching
  matched_location_id: number | null;
  matched_location_name: string | null;
  match_distance_meters: number | null;
  has_location_match: boolean;

  // Reverse geocoding
  reverse_geocode_address: ReverseGeocodeAddress;
  reverse_geocode_display: string;
}

/**
 * Identification result from nameplate image
 */
export interface IdentificationResult {
  // Manufacturer
  manufacturer_id: number | null;
  manufacturer_name: string | null;
  manufacturer_name_raw: string;
  is_new_manufacturer: boolean;
  manufacturer_confidence: number | null;

  // Equipment model
  equipment_model_id: number | null;
  equipment_model_name: string | null;
  model_number_raw: string;
  is_new_model: boolean;
  model_confidence: number | null;

  // Equipment details
  serial_number: string;
  confidence: number;
}

/**
 * Main nameplate scan record
 */
export interface NameplateScan {
  id: string; // UUID
  status: ScanStatus;
  exif_status: TaskStatus;
  identification_status: TaskStatus;
  is_ready: boolean;

  // Image
  image_url: string;
  original_filename: string;

  // Timestamps
  created_at: string;
  updated_at: string;

  // Error handling
  error_message: string;

  // Nested results
  exif_result: ExifResult | null;
  identification_result: IdentificationResult | null;
}

/**
 * Response from POST /api/nameplate-scans/ (upload)
 */
export interface NameplateScanCreateResponse {
  id: string;
  status: ScanStatus;
}

/**
 * Payload for POST /api/nameplate-scans/{id}/assign/
 */
export interface NameplateScanAssignPayload {
  installation_id: number;
}